import { useCallback } from 'react';
import { HEADER_ID } from '@/components/content/Header/constants';
import {
	NAVIGABLE_ACCORDION_SECTION_CLASS_NAME,
	NAVIGABLE_SECTION_IDS,
	NAVIGATION_BAR_ID,
	PRODUCT_BAR_ID,
	SCROLL_TO_NAVIGABLE_ACCORDION_SECTION_EVENT,
} from '@/components/content/ProductDetails/constants';
import {
	calculateScrollToNavigableSectionTopAdjustment,
	getElementOffsetTop,
	getNavigableSectionElement,
} from '@/components/content/ProductDetails/utils';
import { useMediaQuery, useTheme } from '@mui/material';

export type ScrollToNavigableSectionProps = {
	sectionId: NAVIGABLE_SECTION_IDS;
};

/**
 * Provides a function to scroll the page to a registered navigable section (See `useNavigableSection`)
 *
 * The hook calculates the correct scroll position by accounting for
 * fixed layout elements such as the header, navigation bar, and product bar.
 *
 * It also handles special behavior for accordion-based sections by
 * dispatching a custom scroll event instead of scrolling directly.
 */
export const useScrollToNavigableSection = () => {
	const theme = useTheme();
	const isSmScreen = useMediaQuery(theme.breakpoints.down('md'));

	const scrollToNavigableSection = useCallback(
		({ sectionId }: ScrollToNavigableSectionProps) => {
			const sectionElement = getNavigableSectionElement(sectionId);

			if (!sectionElement) {
				console.warn('No section element found for the sectionId', sectionId);
				return;
			}

			const header = document.getElementById(HEADER_ID);
			const navigationBar = document.getElementById(NAVIGATION_BAR_ID);
			const productBar = document.getElementById(PRODUCT_BAR_ID);

			const headerHeight = header?.clientHeight ?? 0;
			const navigationBarHeight = navigationBar?.clientHeight ?? 0;
			const productBarHeight = productBar?.clientHeight ?? 0;

			// TODO: Set NavigationBar tab as active (for additional information this is not needed).

			const offsetTop = getElementOffsetTop(sectionElement);

			const topAdjustment = calculateScrollToNavigableSectionTopAdjustment({
				sectionId,
				isSmScreen,
				headerHeight,
				navigationBarHeight,
				productBarHeight,
			});

			const isAccordionSection = sectionElement.classList.contains(
				NAVIGABLE_ACCORDION_SECTION_CLASS_NAME
			);

			if (isAccordionSection) {
				const event = new CustomEvent(SCROLL_TO_NAVIGABLE_ACCORDION_SECTION_EVENT, {
					detail: {
						sectionId,
						topAdjustment,
					},
				});

				sectionElement.dispatchEvent(event);

				return;
			}

			window.scroll({
				top: offsetTop - topAdjustment,
				// If smooth scrolling does not work:
				// - Ensure the browser supports it and has it enabled.
				// - Check that energy saving mode is not preventing smooth scrolling.
				behavior: 'smooth',
			});
		},
		[isSmScreen]
	);

	return {
		scrollToNavigableSection,
	};
};

export { NAVIGABLE_SECTION_IDS };
